import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';

interface UserProfile {
  id: string;
  user_id: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

interface UserRole {
  role: 'admin' | 'user';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isAdmin: boolean;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch user profile and role
  const fetchUserData = async (userId: string) => {
    // Profile and admin-role are independent lookups. A profile fetch failure
    // must NOT short-circuit the admin-role check — otherwise a legitimate
    // admin whose profiles row is missing (or temporarily hidden by an RLS
    // hiccup) gets silently locked out of /admin. So each runs in its own try.
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (profileError) {
        logger.error('Error fetching profile:', profileError);
      } else {
        setProfile(profileData);
      }
    } catch (error) {
      logger.error('Error fetching profile:', error);
    }

    // Check if user has admin role. maybeSingle() returns null (not an error)
    // when there's no admin row — the normal case for regular users — so we
    // don't log a spurious PGRST116 every time a non-admin signs in.
    try {
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();

      setIsAdmin(!roleError && roleData !== null);
    } catch (error) {
      logger.error('Error fetching role:', error);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener (CRITICAL: No async calls inside callback)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // CRITICAL: Defer async Supabase calls to prevent deadlock
        if (session?.user && mounted) {
          setTimeout(() => {
            if (mounted) {
              fetchUserData(session.user.id);
            }
          }, 0);
        } else {
          setProfile(null);
          setIsAdmin(false);
        }
        
        setLoading(false);
      }
    );

    // Get initial session
    const loadInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (!mounted) return;

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user && mounted) {
        await fetchUserData(session.user.id);
      }
      /* Always clear loading — previously the setLoading(false) only
         fired on the no-session branch, which meant a page refresh with
         a hydrated Supabase session would leave `loading=true` forever
         if the onAuthStateChange listener didn't fire (race on cold
         start). Gated routes (Checkout, /admin) then hung on their
         spinner and the user thought the app was broken. */
      if (mounted) setLoading(false);
    };

    loadInitialSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName || ''
          }
        }
      });

      if (error) {
        toast({
          title: 'Sign Up Error',
          description: error.message,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Sign Up Successful',
          description: 'Please check your email to confirm your account.',
        });
      }

      return { error };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown error occurred');
      toast({
        title: 'Sign Up Error',
        description: errorObj.message,
        variant: 'destructive'
      });
      return { error: errorObj };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        toast({
          title: 'Sign In Error',
          description: error.message,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Welcome back!',
          description: 'Successfully signed in.',
        });
      }

      return { error };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown error occurred');
      toast({
        title: 'Sign In Error',
        description: errorObj.message,
        variant: 'destructive'
      });
      return { error: errorObj };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setSession(null);
      setProfile(null);
      setIsAdmin(false);

      toast({
        title: 'Signed out',
        description: 'Successfully signed out.',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign out';
      toast({
        title: 'Sign Out Error',
        description: errorMessage,
        variant: 'destructive'
      });
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    try {
      if (!user) throw new Error('No user logged in');

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', user.id);

      if (error) throw error;

      setProfile(prev => prev ? { ...prev, ...updates } : null);

      toast({
        title: 'Profile Updated',
        description: 'Your profile has been successfully updated.',
      });

      return { error: null };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown error occurred');
      toast({
        title: 'Update Error',
        description: errorObj.message,
        variant: 'destructive'
      });
      return { error: errorObj };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectTo = `${window.location.origin}/auth/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) {
        toast({ title: 'Reset Error', description: error.message, variant: 'destructive' });
      } else {
        toast({
          title: 'Check your email',
          description: 'We sent a password reset link to ' + email,
        });
      }
      return { error };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown error');
      toast({ title: 'Reset Error', description: errorObj.message, variant: 'destructive' });
      return { error: errorObj };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        toast({ title: 'Update Error', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Password updated', description: 'Your password has been changed.' });
      }
      return { error };
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown error');
      toast({ title: 'Update Error', description: errorObj.message, variant: 'destructive' });
      return { error: errorObj };
    }
  };

  const value = {
    user,
    session,
    profile,
    isAdmin,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};